/**
 * PaymentLinkSection - Section réutilisable pour générer/saisir des liens de paiement Stripe
 * Utilisé dans GestionFinanciereDOMIUM, ValidationVirementsESS et Comptabilité
 */
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CreditCard, Send, Loader2, CheckCircle, Copy, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export function PaymentLinkSection({ reference, amount, customerEmail, customerName, source, description, authHeader, onLinkGenerated }) {
  const [mode, setMode] = useState('auto');
  const [loading, setLoading] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // Champs formulaire - synchronisés avec les props
  const [email, setEmail] = useState(customerEmail || '');
  const [name, setName] = useState(customerName || '');
  const [linkAmount, setLinkAmount] = useState(amount || 0);
  const [manualUrl, setManualUrl] = useState('');
  const [desc, setDesc] = useState(description || 'Paiement contribution');

  // Synchroniser les props quand elles changent
  useEffect(() => { if (customerEmail) setEmail(customerEmail); }, [customerEmail]);
  useEffect(() => { if (customerName) setName(customerName); }, [customerName]);
  useEffect(() => { if (amount) setLinkAmount(amount); }, [amount]);
  useEffect(() => { if (description) setDesc(description); }, [description]);

  // Reset quand la référence change
  useEffect(() => {
    setGeneratedUrl('');
    setEmailSent(false);
  }, [reference]);

  const handleGenerateLink = async () => {
    if (!email) { toast.error('Email du client requis'); return; }
    if (!linkAmount || linkAmount <= 0) { toast.error('Montant invalide'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/payment-links/generate`, {
        reference,
        amount: parseFloat(linkAmount),
        customer_email: email,
        customer_name: name,
        description: desc,
        source: source || 'domium'
      }, { headers: authHeader });
      setGeneratedUrl(res.data.payment_url);
      setEmailSent(res.data.email_sent);
      toast.success(res.data.message);
      if (onLinkGenerated) onLinkGenerated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la génération du lien');
    } finally {
      setLoading(false);
    }
  };

  const handleSendManualLink = async () => {
    if (!manualUrl) { toast.error('URL du lien requis'); return; }
    if (!email) { toast.error('Email du client requis'); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/payment-links/send`, {
        reference,
        payment_url: manualUrl,
        customer_email: email,
        customer_name: name,
        amount: parseFloat(linkAmount) || 0,
        description: desc,
        source: source || 'domium'
      }, { headers: authHeader });
      setEmailSent(res.data.email_sent);
      setGeneratedUrl(manualUrl);
      toast.success(res.data.message);
      if (onLinkGenerated) onLinkGenerated(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Lien copié dans le presse-papiers');
  };

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-200 p-4 rounded-lg space-y-3" data-testid="payment-link-section">
      <div className="flex items-center gap-2">
        <CreditCard className="h-5 w-5 text-emerald-700" />
        <span className="text-sm font-semibold text-emerald-800">Lien de paiement Stripe</span>
      </div>

      {/* Tabs auto/manual */}
      <div className="flex bg-white rounded-md border overflow-hidden">
        <button
          type="button"
          data-testid="tab-auto-link"
          className={`flex-1 text-xs py-1.5 px-3 font-medium transition-colors ${mode === 'auto' ? 'bg-emerald-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
          onClick={() => setMode('auto')}
        >
          Generer automatiquement
        </button>
        <button
          type="button"
          data-testid="tab-manual-link"
          className={`flex-1 text-xs py-1.5 px-3 font-medium transition-colors ${mode === 'manual' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
          onClick={() => setMode('manual')}
        >
          Saisir un lien existant
        </button>
      </div>

      {/* Champs communs */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-emerald-700">Email du client *</Label>
          <Input
            data-testid="payment-link-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@exemple.com"
            className="text-sm h-8"
          />
        </div>
        <div>
          <Label className="text-xs text-emerald-700">Nom du client</Label>
          <Input
            data-testid="payment-link-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nom complet"
            className="text-sm h-8"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-emerald-700">Montant (EUR) *</Label>
          <Input
            data-testid="payment-link-amount"
            type="number"
            step="0.01"
            value={linkAmount}
            onChange={(e) => setLinkAmount(e.target.value)}
            className="text-sm h-8"
          />
        </div>
        <div>
          <Label className="text-xs text-emerald-700">Description</Label>
          <Input
            data-testid="payment-link-desc"
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            className="text-sm h-8"
          />
        </div>
      </div>

      {/* Mode automatique */}
      {mode === 'auto' && (
        <Button
          type="button"
          data-testid="generate-stripe-link-btn"
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm h-9"
          disabled={loading || !email || !linkAmount}
          onClick={handleGenerateLink}
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CreditCard className="h-4 w-4 mr-2" />}
          Generer et envoyer le lien Stripe
        </Button>
      )}

      {/* Mode manuel */}
      {mode === 'manual' && (
        <div className="space-y-2">
          <div>
            <Label className="text-xs text-emerald-700">URL du lien Stripe *</Label>
            <Input
              data-testid="manual-stripe-url"
              value={manualUrl}
              onChange={(e) => setManualUrl(e.target.value)}
              placeholder="https://checkout.stripe.com/..."
              className="text-sm h-8 font-mono"
            />
          </div>
          <Button
            type="button"
            data-testid="send-manual-link-btn"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm h-9"
            disabled={loading || !manualUrl || !email}
            onClick={handleSendManualLink}
          >
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Envoyer le lien par email
          </Button>
        </div>
      )}

      {/* Résultat */}
      {generatedUrl && (
        <div className="bg-white border border-emerald-300 rounded-md p-3 space-y-2" data-testid="payment-link-result">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">
              Lien {emailSent ? 'envoye par email' : 'genere'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Input
              readOnly
              value={generatedUrl}
              className="text-xs font-mono h-7 flex-1 bg-gray-50"
            />
            <Button type="button" variant="outline" size="sm" className="h-7 px-2" onClick={() => copyToClipboard(generatedUrl)}>
              <Copy className="h-3 w-3" />
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-7 px-2" onClick={() => window.open(generatedUrl, '_blank')}>
              <ExternalLink className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
